// hooks/useDisplayMode.js
// Custom hook that toggles between "cards" and "lines" views.
// • On **mobile** screens (≤ 768 px) it is *always* "cards".
// • On wider screens the user can pick either view and the choice is
//   persisted in localStorage.
// ------------------------------------------------------------------

import { useState, useEffect } from "react";

/**
 * Utility: true when the viewport width is ≤ 768 px (same as CSS breakpoint).
 */
const isMobileViewport = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

export default function useDisplayMode() {
  /*
   * ──────────────────────────────────────────────────────────────
   *  INITIAL VALUE
   *  ─ Mobile  → always "cards".
   *  ─ Desktop → use last choice from localStorage, fallback to "cards".
   * ──────────────────────────────────────────────────────────────
   */
  const [mode, setMode] = useState(() =>
    isMobileViewport() ? "cards" : localStorage.getItem("displayMode") || "cards"
  );

  /*
   * Whenever the viewport crosses the mobile breakpoint we enforce the rule:
   *   • Entering mobile  → switch to "cards".
   *   • Leaving  mobile  → keep current (user may change later).
   */
  useEffect(() => {
    const handleResize = () => {
      if (isMobileViewport()) {
        setMode("cards");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /*
   * Persist selection only when **not** on mobile – otherwise the forced
   * value would overwrite the desktop preference repeatedly.
   */
  useEffect(() => {
    if (!isMobileViewport()) {
      localStorage.setItem("displayMode", mode);
    }
  }, [mode]);

  /*
   * Guard setter: on mobile ignore attempts to switch to "lines".
   */
  const safeSetMode = (next) => {
    if (isMobileViewport() && next === "lines") return; // mobile ⇒ always cards
    setMode(next);
  };

  return [mode, safeSetMode];
}
